import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useParams } from "react-router";
import { CompanyLogo } from "@/pages/company/CompanyLogo";
import { useCompanyContext } from "@/pages/company/use-company-context";
import { BackButton } from "@/components/common/BackButton";
import { MarkdownText } from "@/components/common/MarkdownText";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { MenuItem } from "@/components/design/MenuItem";
import { NoBackend } from "@/components/common/NoBackend";
import { ShareIcon } from "@/components/share/ShareItemCard";
import type { ShareIconName } from "@/components/share/ShareItemCard";
import { ShareUrlButton } from "@/components/share/ShareUrlButton";
import { ShareItemAttachments } from "@/components/share/ShareItemAttachments";
import { ShareDetailStatus } from "@/components/share/ShareDetailStatus";
import { SharedItemAuthor } from "@/components/share/SharedItemAuthor";
import { creatorName, fiveStarRounded, itemTypeLabel } from "@/lib/share-item";
import { formatDateTime } from "@/lib/format-date";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { logApplicationFailure, userErrorMessage } from "@/lib/user-error";
import { LocalizedError } from "@/lib/localized-error";
import { usePendingEdits, usePendingScope } from "@/lib/pending-edits";
import {
  actingAsCompanyOnOwnContentMessage,
  answersForItem,
  companyFrozenCommentMessage,
  companyFrozenMessage,
  companyFrozenReaderMessage,
  companyStanding,
  mayManageComment,
  mayManageItem,
  notActingAsCompanyMessage,
  type CompanyStanding,
  type ShareItemLike,
} from "@/lib/share-item";
import {
  CreateMatchDocument,
  MatchMode as GqlMatchMode,
  MyselfDocument,
  SharedItemAuthorAvatarDocument,
  TakeOverSharedItemDocument,
  ViewSharedItemDocument,
} from "@/graphql/generated/operations";
import { usePin } from "@/pages/save/use-pin";
import { NewMatchModal } from "@/components/match/NewMatchModal";
import type { NewMatchDraft } from "@/components/match/NewMatchModal";
import { dataToDesign } from "@/components/match/match-design-mapping";
import { EMPTY_MATCH_DATA } from "@/components/match/match-design-rules";
import { throwIfValidationRejected, useValidationFallback } from "@/components/match/use-match-editor";
import { useSharedItem } from "./use-shared-item";
import { useSharedItemComments } from "./use-shared-item-comments";

// SHARE · Discover - detail view of a shared item: meta + owner actions
// (edit/delete) on the left, content (title · image gallery · description)
// in the middle, comments on the right.
//
// "Interesse zeigen" toggles signedUp via signUpSharedProject; the comment menu's
// "Bearbeiten" edits inline via updateSharedItemComment. Watching is modelled
// through pinning (pinned = "Beobachtet"): the Watch button calls addPins /
// removePins on this item. watchSharedItem is intentionally unused.

const imgSrc = (f: { mimeType?: string | null; content?: string | null } | null | undefined): string | null =>
  f?.content ? `data:${f.mimeType || "image/jpeg"};base64,${f.content}` : null;

export function ShareItemDetailPage() {
  const intl = useIntl();
  const validationFallback = useValidationFallback();
  const { id: itemId = "" } = useParams();
  const navigate = useNavigate();
  const sharedItem = useSharedItem(itemId);
  const { item, loading, error, busy: deleting } = sharedItem;
  // WHICH COMPANY THE VIEWER IS ACTING AS, read here because concept v23 (0002 §6, Kevin 2026-09-11,
  // extended 2026-09-12) made it half of the predicate that decides Edit and Delete on EITHER kind of
  // item: a company item wants that company's context, a personal one wants none at all. The resolved
  // id, so a stored one with no admin membership behind it offers nothing (see use-company-context).
  const actingAsCompanyId = useCompanyContext().active?.id ?? null;
  const mayManage = mayManageItem(item, actingAsCompanyId);
  const author = item?.createdBy;
  // BOTH NAMES, TO EVERYONE WHO CAN SEE THE POST — the company above, the person who wrote it below.
  // That is concept v10 (the company account model): "Beides, nicht statt … die Reihenfolge sagt, wessen Aussage es ist",
  // and the reasoning only works if both names are on the page.
  //
  // IT WAS ADMIN-ONLY FOR THREE DAYS. @adr SPARK-0042-company-post-detail-shows-company-and-operator@2 records the reversal of the rule that narrowed the
  // author to administrators of the owning company. Kevin reversed
  // that on 2026-08-31: "the author of a company's post should be visible to everyone able to see the
  // company who published it". So there is no viewer test here at all — `isAdminOfCompany` still
  // decides who may EDIT, and decides nothing about who is named.
  const publisher = item?.company?.id ?? "";
  const authorAvatar = useQuery(SharedItemAuthorAvatarDocument, {
    variables: { id: author?.id ?? "" },
    skip: !author?.id,
  });

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): the shared item being viewed + the user's
  // relationship to it. Before the early returns below so hook order stays stable.
  useAssistantContext(
    item
      ? [
          {
            description: "the shared item the user is viewing",
            value:
              `"${item.name}" (${item.itemType})` +
              `; ${item.views ?? 0} views, ${item.signUps ?? 0} sign-ups, ${item.ratings ?? 0} ratings` +
              `; the user ${mayManage ? "may manage this" : "may not manage this"}` +
              (item.pinned ? ", is watching it" : "") +
              (item.signedUp ? ", has signed up" : ""),
          },
        ]
      : null,
  );

  // Guided tour (/tour): this shared item — content, stats, and the action buttons. Same
  // hooks-before-early-returns placement as the context call above.
  useAssistantTour(
    item
      ? {
          label: intl.formatMessage({
            id: "share.itemDetail.tour.label",
            defaultMessage: "Share Post",
          }),
          summary: intl.formatMessage(
            {
              id: "share.itemDetail.tour.summary",
              defaultMessage:
                'You are looking at "{name}", a {type} from the Share community.',
            },
            { name: item.name ?? "–", type: itemTypeLabel(item.itemType) },
          ),
          stops: [
            {
              anchor: "share.item.content",
              title: intl.formatMessage({
                id: "share.itemDetail.tour.content.title",
                defaultMessage: "The content",
              }),
              description: intl.formatMessage(
                {
                  id: "share.itemDetail.tour.content.desc",
                  defaultMessage:
                    'Title, image gallery and full description of "{name}". Click the thumbnails to browse the images.',
                },
                { name: item.name ?? "–" },
              ),
            },
            {
              anchor: "share.item.stats",
              title: intl.formatMessage({
                id: "share.itemDetail.tour.stats.title",
                defaultMessage: "How it's doing",
              }),
              description: intl.formatMessage(
                {
                  id: "share.itemDetail.tour.stats.desc",
                  defaultMessage:
                    "The item's reach so far: {views} views, {pins} pins and {signUps} interested people.",
                },
                {
                  views: item.views ?? 0,
                  pins: item.watches ?? 0,
                  signUps: item.signUps ?? 0,
                },
              ),
            },
            {
              anchor: "share.item.actions",
              title: intl.formatMessage({
                id: "share.itemDetail.tour.actions.title",
                defaultMessage: "Get involved",
              }),
              description:
                !answersForItem(item) && item.itemType === "PROJECT"
                  ? intl.formatMessage({
                      id: "share.itemDetail.tour.actions.projectDesc",
                      defaultMessage:
                        "Watch the item to follow it, express interest to sign up as a participant, or start a new match linked to this project.",
                    })
                  : intl.formatMessage({
                      id: "share.itemDetail.tour.actions.desc",
                      defaultMessage:
                        "Watch the item to follow it, or start a new match linked to it.",
                    }),
            },
          ],
        }
      : null,
  );

  const gallery: Array<{ __typename: "File"; mimeType: string | null; content: string }> = [
    item?.mainImage,
    ...(item?.images ?? []),
  ].filter(
    (f): f is { __typename: "File"; mimeType: string | null; content: string } =>
      Boolean(f?.content),
  );
  const [activeIdx, setActiveIdx] = useState(0);
  const hero = gallery[activeIdx] ?? null;

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // The one refusal worth naming here. Everything else already goes to the diagnostics log; a frozen
  // company is the only case where the user can actually do something about it.
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Applying to and rating a frozen company's project are refused for EVERY caller, not only admins,
  // and those items stay listed on the company page — so an ordinary reader can reach the refusal.
  const [actionError, setActionError] = useState<string | null>(null);
  const [signingUp, setSigningUp] = useState(false);
  const [newMatchOpen, setNewMatchOpen] = useState(false);
  const [watching, setWatching] = useState(false);
  const pin = usePin();

  const [viewItem] = useMutation(ViewSharedItemDocument);
  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current || !itemId) return;
    viewedRef.current = true;
    // SWALLOWED ON PURPOSE, and it needs a catch rather than a bare `void`. Recording a view is a
    // side effect the reader never asked for, so a failure must not reach them — but since concept
    // v9 an INACTIVE company's item refuses this for EVERY caller, and those items stay reachable
    // because the company page still lists them. Without a handler that refusal became an unhandled
    // rejection on an ordinary page visit, which the runtime diagnostics then logged as an error.
    void viewItem({ variables: { input: { sharedItemId: itemId } } }).catch(() => {});
  }, [itemId, viewItem]);

  // Match-starten-from-SharedItem entry point per @adr SPARK-0006-project-as-conceptual-entity@1. The new Match is
  // Linked-from-birth + pre-copy: createMatch sets sharedItem, then
  // takeOverSharedItem performs the server-side copy of project.* — the same
  // mutation the in-match "Übernehmen" button uses, so behavior stays in lockstep.
  const [createMatch] = useMutation(CreateMatchDocument);
  const [takeOverSharedItem] = useMutation(TakeOverSharedItemDocument);
  async function startLinkedMatch(draft: NewMatchDraft) {
    if (!item) return;
    const result = await createMatch({
      variables: {
        input: {
          data: {
            sharedItem: { id: item.id },
            matchMode: draft.matchMode as GqlMatchMode,
            name: draft.name || null,
            description: draft.description || null,
            access: draft.access.map((a) => ({
              person: { id: a.personId },
              prepare: a.prepare,
              design: a.design,
              discover: a.discover,
              match: a.match,
            })),
            // Born-complete like the normal Match entry (MatchPage): teamProfile
            // + teamSize/teamCount present so the solver's non-null checks pass
            // on first solve without a prior design edit.
            design: dataToDesign(EMPTY_MATCH_DATA),
          },
        },
      },
    });
    const payload = result.data?.createMatch;
    throwIfValidationRejected(payload, "ValidateMatchResult", validationFallback);
    if (payload?.__typename !== "CreateMatchResult") return;
    const matchId = payload.result.id;
    await takeOverSharedItem({
      variables: { input: { matchId, sharedItemId: item.id } },
    });
    navigate(`/match/${matchId}`);
  }

  async function runDelete() {
    if (deleting || !item) return;
    setDeleteError(null);
    // The two states share one span, so each path clears the other: without this, a delete that
    // failed a minute ago keeps its sentence on screen while a later rating refusal is swallowed.
    setActionError(null);
    try {
      await sharedItem.remove();
      navigate("/extended-search?type=SHARE");
    } catch (e) {
      // EVERY refusal is shown, not only the actionable one. Naming the frozen-company case is the
      // point — it is the one the user can do something about — but falling through to nothing for
      // all the others recreated the very defect this catch was written for: a revoked membership or
      // a network blip left the Yes/No pair sitting there, re-enabled and unchanged, and the button
      // simply did not work. The page-level banner used to cover that and no longer does, because
      // `useSharedItem` now reports only the READ's error (a refused write must not blank the item).
      setDeleteError(
        companyFrozenMessage(intl, e) ??
          // AND THE STALE-CONTEXT REFUSAL, which is reachable precisely BECAUSE the button is hidden
          // for it: the affordance is decided at render and the context can be left in another tab
          // pane before Yes is pressed (concept v23). The sentence names the control by the label it
          // actually carries — "Act as this company" — so the reader can act on it; the generic
          // permission sentence is not something anybody can act on.
          notActingAsCompanyMessage(intl, e) ??
          // AND THE MIRROR REFUSAL, reachable the same way and repaired the other way round: this
          // page can be rendered on one's own post and submitted after a second pane entered a
          // company context, and the sentence then has to say "Leave company context" rather than
          // send the reader into one.
          actingAsCompanyOnOwnContentMessage(intl, e) ??
          userErrorMessage(intl, e),
      );
      logApplicationFailure("share.delete-item");
    }
  }

  async function toggleWatch() {
    if (watching || !item) return;
    setWatching(true);
    try {
      if (item.pinned) {
        await pin.remove({ sharedItemIds: [item.id] });
      } else {
        await pin.add({ sharedItemIds: [item.id] });
      }
      await sharedItem.refetch();
    } catch {
      logApplicationFailure("share.toggle-watch");
    } finally {
      setWatching(false);
    }
  }

  async function toggleSignUp() {
    if (signingUp || !item) return;
    setSigningUp(true);
    setActionError(null);
    setDeleteError(null);
    try {
      await sharedItem.signUp(!item.signedUp);
    } catch (e) {
      // Same fall-through as the delete, with the READER's wording: signing up is not an admin act,
      // so the person who meets this refusal cannot reactivate anything and must not be told to.
      setActionError(companyFrozenReaderMessage(intl, e) ?? userErrorMessage(intl, e));
      logApplicationFailure("share.toggle-sign-up");
    } finally {
      setSigningUp(false);
    }
  }

  const authorName = creatorName(author) || "–";

  return (
    <main
      className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="SharedItemDetail, SharedItemAuthorAvatar, SharedItemFileContents, DeleteSharedItem, RateSharedItem, SignUpSharedProject, AddPins, RemovePins, ViewSharedItem, SharedItemComments, CreateSharedItemComment, UpdateSharedItemComment, DeleteSharedItemComment, Myself, UpdateSharedItem"
    >
      <div className="mb-5">
        <BackButton />
      </div>
      {loading || error || !item ? (
        <ShareDetailStatus
          loading={loading}
          error={error}
          notFoundLabel={intl.formatMessage({ id: "share.itemDetail.notFound", defaultMessage: "Post not found" })}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 @lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)_minmax(260px,320px)] @lg:items-start">
            {/* LEFT - Meta */}
            <aside>
              <div className="box bg-surface p-5">
                <div className="space-y-4">
                  <div>
                    {/* WHOSE POST THIS IS. The content belongs to the company and the company
                        answers for it; the line survives the author's erasure, because it comes
                        from `company_id` and not from them. A personal post shows nothing here —
                        not an empty "Published by".

                        Concept v10 (the company account model) asks for the author beneath it as well, "both, not
                        instead", and that is what happens — for every reader. @adr SPARK-0042-company-post-detail-shows-company-and-operator@2 supersedes
                        the short-lived admin-only rule. See the note below the company block. */}
                    {item.company ? (
                      <div id="shareitem-publisher" className="mb-2">
                        <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
                          <FormattedMessage
                            id="share.itemDetail.publishedBy"
                            defaultMessage="Published by"
                          />
                        </div>
                        <Link
                          id={`shareitem-publisher-${item.company.id}`}
                          to={`/c/${item.company.id}`}
                          className="flex items-center gap-2 rounded text-smycles-body font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
                        >
                          {/* The same component the company page and the company list draw, so the
                              monogram fallback for a company with no logo is decided in one place
                              (0006 §1) rather than invented a third time here. */}
                          <CompanyLogo logo={item.company.logo} name={item.company.name} size="row" />
                          <span className="min-w-0 truncate">{item.company.name}</span>
                        </Link>
                      </div>
                    ) : null}
                    {/* A COMPANY'S POST NAMES ITS OPERATOR, QUIETLY, TO EVERYONE WHO CAN SEE THE
                        POST. One small line under the company — no avatar and no eyebrow of its own,
                        because it is provenance rather than a second author of equal standing — and
                        it is gated on `publisher` alone, with no viewer test.

                        REVERSED ON 2026-08-31 (Kevin: "the author of a company's post should be
                        visible to everyone able to see the company who published it"). @adr SPARK-0042-company-post-detail-shows-company-and-operator@2
                        records that decision and supersedes Spark's short-lived admin-only rule. Do
                        not reintroduce a viewer gate here without a new superseding ADR.

                        A PERSONAL post is untouched: the author IS the publisher there, so it keeps
                        the label and the face. */}
                    {publisher ? (
                      <p className="smycles-field-meta text-ink-muted">
                        {author ? (
                          <Link
                            to={`/p/${author.id}`}
                            className="underline-offset-2 hover:underline"
                          >
                            <FormattedMessage
                              id="share.itemDetail.postedByOperator"
                              defaultMessage="Posted by {name}"
                              values={{ name: authorName }}
                            />
                          </Link>
                        ) : (
                          <FormattedMessage
                            id="share.itemDetail.postedByOperator"
                            defaultMessage="Posted by {name}"
                            values={{ name: authorName }}
                          />
                        )}
                      </p>
                    ) : (
                      <>
                        <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
                          <FormattedMessage id="share.itemDetail.author" defaultMessage="Author" />
                        </div>
                        <SharedItemAuthor
                          name={authorName}
                          person={
                            author
                              ? {
                                  id: author.id,
                                  avatarContent: authorAvatar.data?.person?.avatar?.content,
                                  avatarMime: authorAvatar.data?.person?.avatar?.mimeType,
                                }
                              : null
                          }
                        />
                      </>
                    )}
                  </div>
                  <div>
                    <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
                      <FormattedMessage id="share.itemDetail.publishedOn" defaultMessage="Published on" />
                    </div>
                    <div className="text-smycles-body">{formatDateTime(item.publishedOn, intl.locale) || "–"}</div>
                  </div>
                  <RatingBlock
                    item={item}
                    rate={async (stars) => {
                      setActionError(null);
                      setDeleteError(null);
                      try {
                        await sharedItem.rate(stars);
                      } catch (e) {
                        // The stars snap back to zero on a rejection, which without a message reads
                        // as "the click was lost" rather than "the rating was refused".
                        // The reader's wording: rating is not an admin act either.
                        setActionError(
                          companyFrozenReaderMessage(intl, e) ??
                            userErrorMessage(intl, e),
                        );
                        throw e;
                      }
                    }}
                    onRated={() => void sharedItem.refetch()}
                  />
                  {/* WHERE THE REFUSAL WAS RAISED. Sign-up and rating are both aside controls, so
                      their one shared error line belongs under them rather than in the middle
                      column's toolbar. Neutral text, not a warning ring: a refused rating is a
                      statement about the company's state, not a fault of the reader's
                      (docs/UI_CONVENTIONS.md). */}
                  {actionError ? (
                    <p id="shareitem-actionerror" className="text-smycles-micro text-ink">{actionError}</p>
                  ) : null}
                </div>

                <div
                  className="mt-5 space-y-2.5 border-t border-ink/5 pt-4"
                  data-assistant-anchor="share.item.stats"
                >
                  <Stat
                    id="share-stat-views"
                    icon="eye"
                    label={intl.formatMessage({ id: "share.itemDetail.statViews", defaultMessage: "Views" })}
                    value={item.views}
                  />
                  <Stat
                    id="share-stat-pins"
                    icon="pin"
                    label={intl.formatMessage({ id: "share.itemDetail.statPins", defaultMessage: "Pins" })}
                    value={item.watches}
                  />
                  <Stat
                    id="share-stat-interested"
                    icon="hand"
                    label={intl.formatMessage({ id: "share.itemDetail.statInterested", defaultMessage: "Interested" })}
                    value={item.signUps}
                  />
                </div>

                <div className="mt-5 space-y-2" data-assistant-anchor="share.item.actions">
                  <Button
                    id="share-watch"
                    variant="primary"
                    // Ein Knopf, zwei Zustände: der Zustand steht in aria-pressed,
                    // nicht nur in der Beschriftung. Sonst lässt er sich nur über
                    // seinen übersetzten Text unterscheiden.
                    aria-pressed={Boolean(item.pinned)}
                    onClick={() => void toggleWatch()}
                    disabled={watching}
                    className="w-full disabled:opacity-50"
                  >
                    {watching ? (
                      "…"
                    ) : item.pinned ? (
                      <FormattedMessage
                        id="share.itemDetail.unwatch"
                        defaultMessage="✓ Watching - remove"
                      />
                    ) : (
                      <FormattedMessage id="share.itemDetail.watch" defaultMessage="Watch" />
                    )}
                  </Button>
                  {!answersForItem(item) && item.itemType === "PROJECT" ? (
                    <Button
                      id="share-interest"
                      variant="primary"
                      aria-pressed={Boolean(item.signedUp)}
                      onClick={() => void toggleSignUp()}
                      disabled={signingUp}
                      className="w-full disabled:opacity-50"
                    >
                      {signingUp ? (
                        "…"
                      ) : item.signedUp ? (
                        <FormattedMessage
                          id="share.itemDetail.signedUpWithdraw"
                          defaultMessage="✓ Interested - withdraw"
                        />
                      ) : (
                        <FormattedMessage id="share.itemDetail.expressInterest" defaultMessage="Express interest" />
                      )}
                    </Button>
                  ) : null}
                  <Button
                    variant="primary"
                    onClick={() => setNewMatchOpen(true)}
                    className="w-full disabled:opacity-50"
                    title={intl.formatMessage({
                      id: "share.itemDetail.linkedMatchTitle",
                      defaultMessage: "Start a new match linked to this share item",
                    })}
                  >
                    <FormattedMessage id="share.itemDetail.newMatch" defaultMessage="New match ↗" />
                  </Button>
                  {newMatchOpen && (
                    <NewMatchModal
                      onCreate={startLinkedMatch}
                      onClose={() => setNewMatchOpen(false)}
                    />
                  )}
                </div>
              </div>
            </aside>

            {/* MIDDLE - Content */}
            <section data-assistant-anchor="share.item.content">
              <div className="box bg-surface p-5 @sm:p-6">
                <div className="flex items-start justify-between gap-3">
                  <span className="inline-block rounded bg-accent px-1.5 py-0.5 text-smycles-micro uppercase tracking-widest text-ink">
                    {itemTypeLabel(item.itemType)}
                  </span>
                  <div className="-mt-1 flex items-center gap-1.5">
                    <ShareUrlButton
                      path={`/share/discover/${item.id}`}
                      title={intl.formatMessage({ id: "share.itemDetail.share", defaultMessage: "Share" })}
                      className={buttonClasses({ variant: "secondary", size: "compact" })}
                    >
                      <FormattedMessage id="share.itemDetail.shareWithIcon" defaultMessage="↗ Share" />
                    </ShareUrlButton>
                    {/* DELETE'S REFUSAL ONLY. Sign-up and rating live in the aside, and their
                        refusals used to surface here — a sentence about a rating appearing beside
                        Share/Edit/Delete, a column away from the stars that raised it, which reads
                        as an unrelated failure. `actionError` now renders under the block that
                        raised it; this one stays because Delete is right here. */}
                    {deleteError ? (
                      <span id="shareitem-deleteerror" className="text-smycles-micro text-ink">{deleteError}</span>
                    ) : null}
                    {mayManage && confirmingDelete ? (
                      <>
                        <span className="text-smycles-micro text-ink-muted">
                          <FormattedMessage id="share.itemDetail.deleteConfirm" defaultMessage="Delete?" />
                        </span>
                        <Button
                          id="share-item-delete-confirm"
                          variant="destructive"
                          size="sm"
                          onClick={() => void runDelete()}
                          disabled={deleting}
                        >
                          <FormattedMessage id="share.itemDetail.yes" defaultMessage="Yes" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmingDelete(false)}
                        >
                          <FormattedMessage id="share.itemDetail.no" defaultMessage="No" />
                        </Button>
                      </>
                    ) : null}
                    {mayManage && !confirmingDelete ? (
                      <>
                        <ActionBtn
                          id="share-item-edit"
                          icon="pencil"
                          title={intl.formatMessage({ id: "common.edit", defaultMessage: "Edit" })}
                          onClick={() => navigate(`/share/edit/${item.id}`)}
                        />
                        <ActionBtn
                          id="share-item-delete"
                          icon="trash"
                          title={intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" })}
                          danger
                          onClick={() => setConfirmingDelete(true)}
                        />
                      </>
                    ) : null}
                  </div>
                </div>
                <h1 id="share-item-title" className="smycles-page-title mt-3">{item.name || "–"}</h1>
                {item.shortDescription ? (
                  <p id="share-item-short" className="mt-3 max-w-2xl text-smycles-body leading-relaxed text-ink">{item.shortDescription}</p>
                ) : null}

                {hero ? (
                  <div className="mt-5">
                    <div className="overflow-hidden rounded-md bg-ink/[0.06] ring-1 ring-ink/5">
                      <img
                        id="share-item-hero"
                        src={imgSrc(hero) ?? ""}
                        alt={item.name || ""}
                        className="aspect-[16/9] w-full object-cover"
                      />
                    </div>
                    {gallery.length > 1 ? (
                      <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                        {gallery.map((f, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setActiveIdx(i)}
                            title={intl.formatMessage(
                              { id: "share.itemDetail.imageN", defaultMessage: "Image {n}" },
                              { n: i + 1 },
                            )}
                            className={`shrink-0 overflow-hidden rounded-md ring-1 transition ${
                              i === activeIdx ? "smycles-selected ring-2 ring-accent-decoration" : "ring-ink/10 hover:ring-accent-decoration/40"
                            }`}
                          >
                            <img src={imgSrc(f) ?? ""} alt="" className="h-16 w-24 object-cover" />
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="mt-6 border-t border-ink/5 pt-5">
                  {item.description ? (
                    <MarkdownText content={item.description} className="text-smycles-body leading-relaxed text-ink" />
                  ) : (
                    <NoBackend
                      label={intl.formatMessage({ id: "share.itemDetail.noDescription", defaultMessage: "No description" })}
                      hint={intl.formatMessage({ id: "share.itemDetail.noDescriptionHint", defaultMessage: "no content provided" })}
                    />
                  )}
                </div>

                <ShareItemAttachments
                  files={sharedItem.attachments}
                  onOpen={sharedItem.openFile}
                  onDownload={sharedItem.downloadFile}
                />
              </div>
            </section>

            {/* RIGHT - Comments */}
            <aside className="@lg:sticky @lg:top-6">
              {/* THE ITEM'S OWN COMPANY TRAVELS DOWN, because concept v20 (a) made it part of the
                  answer: every comment on an inactive company's content is frozen now, including a
                  private one by somebody with nothing to do with that company. Which of the two
                  frozen sentences a refusal gets therefore depends on the ITEM as well as on the
                  comment. */}
              <CommentArea itemId={item.id} itemStanding={companyStanding(item)} />
            </aside>
          </div>

        </>
      )}
    </main>
  );
}

function Stat({
  icon,
  label,
  value,
  id,
}: {
  icon: ShareIconName;
  label: string;
  value: number | null | undefined;
  /** Testanker auf der Zahl (UI_CONVENTIONS → Test anchors). */
  id: string;
}) {
  return (
    <div className="flex items-center gap-2.5 text-smycles-meta">
      <span id={id} className="w-6 tabular-nums text-ink">{Number(value ?? 0)}</span>
      <ShareIcon name={icon} className="h-5 w-5 text-ink-muted" />
      <span className="text-ink-muted">{label}</span>
    </div>
  );
}

function ActionBtn({
  id,
  icon,
  title,
  danger = false,
  onClick,
  disabled = false,
  size = "md",
}: {
  id?: string;
  icon: ShareIconName;
  title: string;
  danger?: boolean;
  onClick: () => void;
  disabled?: boolean;
  size?: "sm" | "md";
}) {
  const dim = size === "sm" ? "h-7 w-7" : "h-9 w-9";
  const ico = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
  return (
    <button
      id={id}
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex ${dim} items-center justify-center rounded-full ring-1 ring-ink/15 transition disabled:opacity-40 ${
        danger
          ? "text-ink-muted hover:bg-red-600 hover:text-white hover:ring-red-600"
          : "text-ink hover:bg-ink hover:text-ink"
      }`}
    >
      <ShareIcon name={icon} className={ico} />
    </button>
  );
}

function RatingBlock({
  item,
  rate,
  onRated,
}: {
  item: ShareItemLike;
  rate: (fiveStarRating: number) => Promise<void>;
  onRated: () => void;
}) {
  const intl = useIntl();
  // Not just "somebody else wrote it": an admin must not rate their own company's content
  // either, and genesis refuses it silently, so offering the control would be a dead end.
  //
  // `answersForItem` RATHER THAN `mayManageItem`, since concept v23 split the two. v23 narrowed
  // editing and deleting to an admin who is also ACTING as the company; rating is untouched by it, so
  // reading the narrowed predicate here would hand that same admin a star rating on their own
  // company's post the moment they left the context — widening a control v23 never mentioned.
  const canRate = !answersForItem(item);
  const [open, setOpen] = useState(false);

  return (
    <div>
      <div className="mb-1.5 text-smycles-micro tracking-[0.25em] text-ink-muted">
        <FormattedMessage id="share.itemDetail.rating" defaultMessage="Rating" />
      </div>
      <button
        id="share-rating"
        type="button"
        onClick={canRate ? () => setOpen((o) => !o) : undefined}
        title={
          canRate
            ? intl.formatMessage({ id: "share.itemDetail.rateYourselfTitle", defaultMessage: "Click to rate it yourself" })
            : undefined
        }
        className={`group flex items-center gap-2 text-smycles-body ${canRate ? "cursor-pointer" : "cursor-default"}`}
      >
        <ShareIcon name="star" className="h-5 w-5 text-ink" />
        <span className="font-medium tabular-nums">{fiveStarRounded(item.rating)}</span>
        <span id="share-rating-count" className="text-ink-muted">
          <FormattedMessage
            id="share.itemDetail.ratingsCount"
            defaultMessage="({count} ratings)"
            values={{ count: Number(item.ratings ?? 0) }}
          />
        </span>
        {canRate ? (
          <span id="share-rate-toggle" className="text-smycles-micro tracking-[0.15em] text-ink-muted transition group-hover:text-ink-accent">
            {open ? (
              <FormattedMessage id="share.itemDetail.ratingClose" defaultMessage="close" />
            ) : (
              <FormattedMessage id="share.itemDetail.ratingOpen" defaultMessage="rate ›" />
            )}
          </span>
        ) : null}
      </button>
      {canRate && open ? (
        <div className="mt-2.5">
          <RateStars rate={rate} alreadyRated={Boolean(item.rated)} onRated={onRated} />
        </div>
      ) : null}
    </div>
  );
}

function RateStars({
  rate,
  alreadyRated,
  onRated,
}: {
  rate: (fiveStarRating: number) => Promise<void>;
  alreadyRated: boolean;
  onRated: () => void;
}) {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(0);
  const [mine, setMine] = useState(0);
  const shown = hover || mine;

  async function submit(n: number) {
    if (loading) return;
    setMine(n);
    setLoading(true);
    try {
      await rate(n);
      onRated();
    } catch {
      logApplicationFailure("share.rate-item");
      setMine(0);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            disabled={loading}
            id={`share-star-${n}`}
            onMouseEnter={() => setHover(n)}
            onClick={() => void submit(n)}
            title={intl.formatMessage(
              { id: "share.itemDetail.starsTitle", defaultMessage: "{n, plural, one {# star} other {# stars}}" },
              { n },
            )}
            aria-label={intl.formatMessage(
              { id: "share.itemDetail.starsAriaLabel", defaultMessage: "{n} of 5 stars" },
              { n },
            )}
            className="text-smycles-title leading-none transition disabled:opacity-50"
          >
            <span className={n <= shown ? "text-ink" : "text-ink-muted"}>★</span>
          </button>
        ))}
        {mine > 0 ? (
          <span className="ml-2 text-smycles-label text-ink-muted">
            <FormattedMessage id="share.itemDetail.thanks" defaultMessage="Thanks!" />
          </span>
        ) : null}
      </div>
      {alreadyRated && mine === 0 ? (
        <div className="mt-1 text-smycles-micro text-ink-muted">
          <FormattedMessage
            id="share.itemDetail.alreadyRated"
            defaultMessage="You have already rated, tap again to change."
          />
        </div>
      ) : null}
    </div>
  );
}

function CommentArea({
  itemId,
  itemStanding,
}: {
  itemId: string;
  /**
   * The caller's standing on the company the ITEM is filed under, or null when it is a personal
   * item. Only the refusal sentence reads it: what the composer and the row OFFER is decided per
   * comment, and never by the item (@adr SPARK-0043-a-comment-can-be-written-as-the-company@3).
   */
  itemStanding: CompanyStanding;
}) {
  const intl = useIntl();
  // COMMENTING AS THE COMPANY IS IN SCOPE (concept v16, the company account model, Kevin 2026-09-08). v10 had taken it
  // out on 2026-08-26, and its reason was the table rather than the idea: `shared_item_comment`
  // carried a `person_id` and nothing else, so a comment written here would have been filed silently
  // under the human behind the context. v16 writes the company down instead
  // (`shared_item_comment.company_id`, genesis V0075), which makes the attribution expressible — so
  // the composer stays, and while a company is active the comment is published in its name.
  //
  // The note above the composer is a NEUTRAL statement of the current state, not a warning: it says
  // whose voice this will be, and how to comment as oneself instead.
  const { active: actingAsCompany } = useCompanyContext();
  const commentsHook = useSharedItemComments(itemId);
  const { comments, totalCount: total, loading, error, busy } = commentsHook;
  const { data: myselfData } = useQuery(MyselfDocument);
  const meId = myselfData?.myself?.id ?? null;

  const [text, setText] = useState("");
  // WHY A REFUSED COMMENT NEEDS ITS OWN SENTENCE. @adr SPARK-0043-a-comment-can-be-written-as-the-company@3 requires it in as many
  // words — "Whether a company comment may be written at all while the company is inactive is
  // genesis's answer, not Spark's; the interface SURFACES the refusal rather than predicting it."
  // Concept v16 (the company account model) freezes an INACTIVE company's
  // comments along with everything else at its content, and genesis refuses those three writes with
  // its own code, GENESIS001250 — deliberately not the general one, because "you are not an admin"
  // and "the company is inactive" are repaired in OPPOSITE directions and the caller here already IS
  // an admin. §6: "Eigener Fehlercode, nicht die allgemeine Ablehnung … Eine Oberfläche, die beide
  // nicht auseinanderhalten kann, sagt zwangsläufig das Falsche." Swallowing it into
  // `logApplicationFailure` said nothing at all, which is worse than either.
  //
  // AND SINCE CONCEPT v20 (a) THE CALLER IS NOT NECESSARILY AN ADMIN. The freeze covers the whole
  // thread now — every comment on an inactive company's content, "auch kein **privater**, auch nicht
  // von seiner eigenen Autorin" — so the sentence above, which ends in "reactivate the company", can
  // land on somebody with no rights over it. `companyFrozenCommentMessage` picks the reader variant
  // for exactly that person; the three call sites below hand it the standings it needs.
  //
  // EVERY refusal is shown, not only the actionable one — the same fall-through the item's delete and
  // sign-up use above, and for the reason recorded there: falling through to nothing left a dead
  // button and no explanation for a revoked membership or a network blip.
  const [writeError, setWriteError] = useState<string | null>(null);

  // A half-written comment is unsaved work like any other: UnsavedGuard asks before ← or a link
  // drops it, the acting-company gate before a switch does. Posting is the composer's own button,
  // so the SaveBar stays out of it; the dialog's Save posts the comment.
  usePendingScope(`share-comment:${itemId}`, {
    dirty: text.trim() !== "",
    ownSaveAction: true,
    save: async () => {
      try {
        await addComment();
      } catch (failure) {
        logApplicationFailure("share.create-comment");
        throw new LocalizedError(addFailureMessage(failure), { cause: failure });
      }
    },
    discard: () => setText(""),
  });

  // ONE POST AT A TIME, for the reason ShareCreatePage's persist() gives: Send is counted as a
  // running save (trackSave in submit), which keeps the dialog's Save closed while a post is in
  // flight, and a call that gets through anyway joins the post rather than sending it twice.
  const pending = usePendingEdits();
  const posting = useRef<Promise<void> | null>(null);
  function addComment(): Promise<void> {
    posting.current ??= (async () => {
      // The context supplies the claim; genesis checks the admin membership against it (the company account model v7 —
      // "beim Anlegen nennt die Aufrufende die Firma"). A personal comment sends nothing.
      await commentsHook.add(text, actingAsCompany?.id ?? null);
      setText("");
    })().finally(() => {
      posting.current = null;
    });
    return posting.current;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setWriteError(null);
    try {
      await (pending ? pending.trackSave(addComment()) : addComment());
    } catch (failure) {
      // Named rather than `e`, which is the submit event one scope up.
      setWriteError(addFailureMessage(failure));
      logApplicationFailure("share.create-comment");
    }
  }

  function addFailureMessage(failure: unknown): string {
    // THE ACTING CONTEXT COUNTS AS ADMINISTERED WITHOUT ASKING, because `useCompanyContext`
    // resolves a context only for a membership row genesis answered with `role = admin` (concept
    // v10, 0002 §5). There is no such thing here as a context over a company one does not
    // administer.
    return (
      companyFrozenCommentMessage(intl, failure, [actingAsCompany ? true : null, itemStanding]) ??
        // v23 refuses a CREATE only for a contradiction — a declared context naming a company
        // other than the one in `data.company`. This composer derives both from the same value, so
        // reaching it means genesis and spark disagree about the context, and the reader still gets
        // the repair rather than "something went wrong".
        notActingAsCompanyMessage(intl, failure) ??
        // AND ITS MIRROR: a create that declares a context must produce content of that company,
        // so a private comment written while acting as one is refused with GENESIS001260 (v23 as
        // Kevin extended it on 2026-09-12). This composer spells both halves from one value, so
        // reaching it means genesis and spark disagree about the context — and the reader is still
        // told which way to move rather than "something went wrong".
        actingAsCompanyOnOwnContentMessage(intl, failure) ??
        userErrorMessage(intl, failure)
    );
  }

  async function onDelete(comment: ShareItemLike) {
    setWriteError(null);
    try {
      await commentsHook.remove(comment.id);
    } catch (e) {
      setWriteError(
        companyFrozenCommentMessage(intl, e, [companyStanding(comment), itemStanding]) ??
          notActingAsCompanyMessage(intl, e) ??
          actingAsCompanyOnOwnContentMessage(intl, e) ??
          userErrorMessage(intl, e),
      );
      logApplicationFailure("share.delete-comment");
    }
  }

  /**
   * The edit path's half of the same reporting. The refusal is caught HERE rather than in the row so
   * the sentence sits with the composer and outlives the row leaving edit mode; the rethrow keeps
   * {@link CommentRow}'s own behaviour intact, which is to stay open with the draft still in it.
   */
  async function onEditComment(comment: ShareItemLike, content: string) {
    setWriteError(null);
    try {
      await commentsHook.edit(comment.id, content);
    } catch (e) {
      const message =
        // THE STALE PAGE, and this is the path it reaches by. The menu is withheld unless the
        // declared context matches the comment — acting as its company for a company comment, acting
        // as nobody for a personal one — so an edit only gets this far when the context moved after
        // the row was drawn. Concept v23 asks for a usable answer exactly there, and BOTH directions
        // are offered because the context can move either way.
        companyFrozenCommentMessage(intl, e, [companyStanding(comment), itemStanding]) ??
          notActingAsCompanyMessage(intl, e) ??
          actingAsCompanyOnOwnContentMessage(intl, e) ??
          userErrorMessage(intl, e);
      setWriteError(message);
      // Localized, because the unsaved-changes dialog shows this rejection when it saves the draft.
      throw new LocalizedError(message, { cause: e });
    }
  }

  return (
    <div className="box flex flex-col overflow-hidden bg-surface">
      <div className="flex items-center justify-between gap-2 border-b border-ink/5 px-4 py-3">
        <div id="share-comment-heading" className="text-smycles-micro tracking-[0.25em] text-ink-muted">
          <FormattedMessage id="share.itemDetail.comments" defaultMessage="Comments" />
        </div>
        <span id="share-comment-count" className="text-smycles-label tabular-nums text-ink-muted">{total}</span>
      </div>

      {actingAsCompany ? (
        <div id="share-comment-blocked" className="border-b border-ink/5 px-3 pt-3 text-smycles-label text-ink">
          <div className="rounded border border-ink/10 bg-ink/[0.02] px-3 py-2">
            <FormattedMessage
              id="share.itemDetail.commentAsCompany"
              defaultMessage="Commenting as {company}. Leave the company context to comment as yourself."
              values={{ company: <span className="font-medium">{actingAsCompany.name}</span> }}
            />
          </div>
        </div>
      ) : null}

      <form onSubmit={submit} className="border-b border-ink/5 p-3">
        <textarea
          id="share-comment-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder={intl.formatMessage({ id: "share.itemDetail.commentPlaceholder", defaultMessage: "Write a comment…" })}
          className="w-full resize-none rounded-md bg-page px-3 py-2 text-smycles-meta leading-relaxed outline-none ring-1 ring-ink/10 transition focus:ring-ink-accent-ui"
        />
        <div className="mt-2 flex justify-end">
          <Button
            id="share-comment-send"
            type="submit"
            variant="primary"
            disabled={busy || !text.trim()}
            className="px-4 py-1.5 disabled:opacity-40"
          >
            {busy ? "…" : <FormattedMessage id="share.itemDetail.send" defaultMessage="Send" />}
          </Button>
        </div>
        {/* BESIDE THE COMPOSER, NOT INSTEAD OF THE THREAD. v16: "Gesperrt ist die Firmenstimme, nicht
            die Diskussion. Der Thread bleibt lesbar" — so a refused write reports itself here, in
            the region that is always in view, while the list below keeps rendering. */}
        {writeError ? <p className="mt-2 text-smycles-micro text-ink">{writeError}</p> : null}
      </form>

      <div className="max-h-[60vh] min-h-[18rem] space-y-4 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="text-smycles-label text-ink-muted">
            <FormattedMessage id="share.itemDetail.loading" defaultMessage="loading…" />
          </div>
        ) : error ? (
          <div className="text-smycles-label text-ink-muted">
            <FormattedMessage
              id="share.itemDetail.error"
              defaultMessage="Error: {message}"
              values={{ message: userErrorMessage(intl, error) }}
            />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-smycles-meta text-ink-muted">
            <FormattedMessage id="share.itemDetail.noComments" defaultMessage="No comments yet, be the first." />
          </div>
        ) : (
          comments.map((c) => (
            <CommentRow
              key={c.id}
              comment={c}
              canManage={mayManageComment(
                c,
                Boolean(meId && c.person?.id === meId),
                actingAsCompany?.id ?? null,
              )}
              busy={busy}
              onEdit={(content) => onEditComment(c, content)}
              onDelete={() => void onDelete(c)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function CommentRow({
  comment,
  canManage = false,
  busy = false,
  onEdit,
  onDelete,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  comment: any;
  /**
   * Whether to offer Edit and Delete — `mayManageComment`'s answer, NOT "did I write this". For a
   * company comment those are different questions (the company account model v8/v16), and conflating them offers a
   * demoted author buttons genesis refuses.
   *
   * <p>Since concept v23 BOTH cases also ask about the acting context, and about the same thing from
   * two sides: an admin reading the thread as themselves is offered nothing on their own company's
   * comment, and — since Kevin extended v23 on 2026-09-12 — an admin acting as a company is offered
   * nothing on their own private one. That reverses the alternative
   * @adr SPARK-0043-a-comment-can-be-written-as-the-company@3 rejected for "hiding a button the
   * server allows" — from v23 the server refuses both.
   */
  canManage?: boolean;
  busy?: boolean;
  onEdit: (content: string) => Promise<void>;
  onDelete: () => void;
}) {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(comment.content ?? "");
  const [saving, setSaving] = useState(false);

  // An open edit with changed text is unsaved work — same scope contract as the composer above.
  usePendingScope(`share-comment-edit:${comment.id}`, {
    dirty: editing && draft.trim() !== (comment.content ?? "").trim(),
    ownSaveAction: true,
    save: async () => {
      const trimmed = draft.trim();
      if (!trimmed) {
        throw new LocalizedError(
          intl.formatMessage({
            id: "share.itemDetail.commentEmpty",
            defaultMessage: "An empty comment cannot be saved.",
          }),
        );
      }
      await sendEdit(trimmed);
    },
    discard: () => {
      setDraft(comment.content ?? "");
      setEditing(false);
    },
  });

  // One edit at a time and counted as a running save — the composer's reasoning, for the row's Save.
  const pending = usePendingEdits();
  const editFlight = useRef<Promise<void> | null>(null);
  function sendEdit(content: string): Promise<void> {
    editFlight.current ??= onEdit(content)
      .then(() => setEditing(false))
      .finally(() => {
        editFlight.current = null;
      });
    return editFlight.current;
  }

  async function submitEdit() {
    const trimmed = draft.trim();
    if (!trimmed || saving) return;
    if (trimmed === (comment.content ?? "").trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await (pending ? pending.trackSave(sendEdit(trimmed)) : sendEdit(trimmed));
    } catch {
      logApplicationFailure("share.edit-comment");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div id={`share-comment-${comment.id}`}>
      {/* WHOSE COMMENT THIS IS. Concept v16 (the company account model): the company goes ABOVE the author and the
          author stays — "Beides, nicht statt", the same shape a company post has carried since v10,
          because the content is the company's and it answers for it while who typed it stays visible.
          A personal comment shows nothing here, not an empty "Published by".

          NO LOGO: genesis resolves `Company.logo` on four surfaces only, and a thread — a list whose
          length the caller does not settle — is deliberately not one of them. Asking for one here
          would draw a monogram for every company comment and tell the reader nothing. */}
      {comment.company ? (
        <div className="mb-0.5 text-smycles-micro text-ink-muted">
          <FormattedMessage
            id="share.itemDetail.commentPublishedBy"
            defaultMessage="Published by {company}"
            values={{
              company: (
                <Link
                  to={`/c/${comment.company.id}`}
                  className="rounded font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
                >
                  {comment.company.name}
                </Link>
              ),
            }}
          />
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2">
        {/* The dash is deliberate for an erased author (v16): genesis answers `createdBy` as null
            also when the reader merely may not see them, so a "person deleted" label would assert
            deletion about living people. */}
        <span className="text-smycles-meta font-medium">{comment.person?.formattedName || "–"}</span>
        <div className="flex shrink-0 items-center gap-1">
          <span className="text-smycles-micro text-ink-muted">{formatDateTime(comment.postedAt, intl.locale) || "–"}</span>
          {canManage ? (
            <div className="relative">
              <button
                id={`share-comment-menu-${comment.id}`}
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                title={intl.formatMessage({ id: "share.itemDetail.more", defaultMessage: "More" })}
                aria-label={intl.formatMessage({ id: "share.itemDetail.more", defaultMessage: "More" })}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="-mr-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-ink-muted transition hover:bg-ink/[0.06] hover:text-ink"
              >
                <ShareIcon name="dots" className="h-4 w-4" />
              </button>
              {menuOpen ? (
                <>
                  <div
                    aria-hidden="true"
                    onClick={() => setMenuOpen(false)}
                    className="fixed inset-0 z-30"
                  />
                  <div
                    role="menu"
                    className="absolute right-0 top-full z-40 mt-1 w-36 overflow-hidden rounded-md bg-surface py-1 shadow-lg ring-1 ring-ink/10"
                  >
                    <MenuItem
                      id={`share-comment-edit-${comment.id}`}
                      role="menuitem"
                      icon={<ShareIcon name="pencil" className="h-3.5 w-3.5" />}
                      onClick={() => {
                        setMenuOpen(false);
                        setDraft(comment.content ?? "");
                        setEditing(true);
                      }}
                    >
                      <FormattedMessage id="common.edit" defaultMessage="Edit" />
                    </MenuItem>
                    <MenuItem
                      id={`share-comment-delete-${comment.id}`}
                      role="menuitem"
                      icon={<ShareIcon name="trash" className="h-3.5 w-3.5" />}
                      disabled={busy}
                      onClick={() => {
                        setMenuOpen(false);
                        onDelete();
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <FormattedMessage id="common.delete" defaultMessage="Delete" />
                    </MenuItem>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* WHO CHANGED IT, AND WHEN — concept v20 (b) (the company account model,
          Kevin 2026-09-10). The row above names the author and the time it was CREATED; this names
          the admin who last edited it and the time of the EDIT. Without it the visible name stops
          being the writer of the visible words the moment a colleague edits, and that byline is the
          very safeguard v16 leans on when it keeps both attributions.

          `updatedAt` IS THE GATE, not `updatedBy`: genesis writes a new comment with the column
          NULL, so a value means a real edit and never a creation. Where it is set but the editor
          cannot be named — an erased person, or one this reader may not see — the ORDINARY DASH goes
          where the name would, exactly as it does for a vanished author. No "person deleted" label:
          genesis answers null for a living person a reader merely may not see.

          COMPANY COMMENTS ONLY, deliberately. genesis stamps `updated_at` on every edit including an
          author fixing their own personal comment, and v20 (b) is explicit that the marker is for
          company comments — "ein privater Kommentar, den seine Autorin selbst korrigiert, bekommt
          keine zweite Zeile". */}
      {comment.company && comment.updatedAt ? (
        <div className="mt-0.5 text-smycles-micro text-ink-muted">
          <FormattedMessage
            id="share.itemDetail.commentEditedBy"
            defaultMessage="Edited by {editor} on {when}"
            values={{
              editor: comment.updatedBy?.formattedName || "–",
              when: formatDateTime(comment.updatedAt, intl.locale) || "–",
            }}
          />
        </div>
      ) : null}

      {editing ? (
        <div className="mt-1.5">
          <textarea
            id={`share-commentinput-${comment.id}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            autoFocus
            className="w-full resize-none rounded-md bg-page px-3 py-2 text-smycles-meta leading-relaxed outline-none ring-1 ring-ink/10 transition focus:ring-ink-accent-ui"
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="px-3 py-1.5 text-smycles-micro disabled:opacity-40"
            >
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              id={`share-commentsave-${comment.id}`}
              variant="primary"
              onClick={() => void submitEdit()}
              disabled={saving || busy || !draft.trim()}
              className="px-3 py-1.5 text-smycles-micro disabled:opacity-40"
            >
              {saving ? "…" : <FormattedMessage id="common.save" defaultMessage="Save" />}
            </Button>
          </div>
        </div>
      ) : (
        <p id={`share-commenttext-${comment.id}`} className="mt-0.5 whitespace-pre-wrap text-smycles-meta leading-relaxed text-ink">{comment.content}</p>
      )}
    </div>
  );
}
